import { DatePicker, Field, FieldLabel, PromptModal } from '@/components'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'

export interface ConfigureFixedAddonPromptForm {
  validFrom: Date | null
  validTo: Date | null
  salaryCode: string | null
  salaryCodeIfOvertime: string | null
}

interface ConfigureFixedAddonPromptProps {
  initialData?: ConfigureFixedAddonPromptForm
  editing?: boolean
  onSubmit: (data: ConfigureFixedAddonPromptForm | null) => void
}

export const ConfigureFixedAddonPrompt: React.FC<
  ConfigureFixedAddonPromptProps
> = ({ initialData, editing = false, onSubmit }) => {
  const translations = useTranslate({
    label: {
      datetimeStart: 'common.start',
      datetimeEnd: 'common.end',
      salaryCode: 'common.salary-code',
      salaryCodeIfOvertime: 'common.salary-code-if-overtime',
    },
    create: 'common.create-alt',
    edit: 'common.edit',
    save: 'common.save',

    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
  })

  const { formValues, formValid, formErrors, updateForm } =
    useForm<ConfigureFixedAddonPromptForm>({
      values: initialData ?? {
        validFrom: null,
        validTo: null,
        salaryCode: '',
        salaryCodeIfOvertime: null,
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        validFrom: (value, other) => {
          if (!value || !other.validTo) return null
          return value <= other.validTo
            ? null
            : translations.error.startBeforeEnd
        },
        validTo: (value, other) => {
          if (!value || !other.validFrom) return null
          return value >= other.validFrom
            ? null
            : translations.error.startBeforeEnd
        },
        salaryCode: value => (value ? null : translations.error.required),
      },
    })

  function updateDate(key: 'validFrom' | 'validTo', value: Date | null) {
    if (!value) {
      updateForm({ [key]: null })
      return
    }
    updateForm({
      [key]: set(value, {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      }),
    })
  }

  const dateError = formErrors.validFrom ?? formErrors.validTo

  return (
    <PromptModal
      title={editing ? translations.edit : translations.create}
      submitText={translations.save}
      submitDisabled={!formValid}
      cancelOnEscape
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(formValues)}
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field>
            <FieldLabel>{translations.label.datetimeStart}</FieldLabel>
            <DatePicker
              mode="single"
              clearable
              format="dd.MM.yyyy"
              className="w-full"
              value={formValues.validFrom}
              onChange={value => updateDate('validFrom', value)}
            />
          </Field>
          <Field>
            <FieldLabel>{translations.label.datetimeEnd}</FieldLabel>
            <DatePicker
              mode="single"
              clearable
              format="dd.MM.yyyy"
              className="w-full"
              value={formValues.validTo}
              onChange={value => updateDate('validTo', value)}
            />
          </Field>
        </div>
        {dateError && <p className="-mt-2 text-sm text-red-600">{dateError}</p>}

        <Field>
          <FieldLabel>{translations.label.salaryCode}</FieldLabel>
          <SalaryCodeCombobox
            mode="single"
            value={formValues.salaryCode ?? ''}
            onChange={salaryCode => updateForm({ salaryCode })}
          />
        </Field>
        <Field>
          <FieldLabel>{translations.label.salaryCodeIfOvertime}</FieldLabel>
          <SalaryCodeCombobox
            mode="single"
            clearable
            value={formValues.salaryCodeIfOvertime ?? ''}
            onChange={code =>
              updateForm({ salaryCodeIfOvertime: code || null })
            }
          />
        </Field>
      </div>
    </PromptModal>
  )
}
